import { memo, useCallback, useMemo, useState } from "react";
import { useStore } from "../../store/globalState";
import { useSessionStore } from "../../store/useSessionStore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AuraLayout,
  BankIcon,
  Button,
  CashSpecialIcon,
  ExchangeV2Icon,
  Layout,
  WalletIcon,
} from "lola-framework-ui-test";
import { M2M_ROUTES, TITLE_COLOR } from "../../constants";
import { FlagCircle } from "../../components/FlagCircle";
import { QuoteInfo } from "../../components/m2m/QuoteInfo";
import { amountFormatter } from "../../utils";
import { PayoutInfo } from "../../components/PayoutInfo";
import { M2mFirstSendWarning } from "../../components/m2m/M2mFirstSendWarning";
import { Popup } from "../../components/Popup";
import { SelectPaymentMethodSlot } from "../../components/SelectPaymentMethodSlot";
import { DestinationAccountInfo } from "../../components/DestinationAccountInfo";
import type { paymentType } from "../../interfaces/payment";
import masterCardLogo from "../../assets/master_card_icon.svg";
import visaLogo from "../../assets/visa_color_icon.svg";
import { useQuote } from "../../hooks/useQuote";
import { COUNTRY_CURRENCY } from "../../hooks/useExchangeRate";
import { MOCK_EXCHANGE_RATE } from "../../gallery/mockData";
import { formatBankAccountLabel } from "../../utils/bankAccountLabel";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";


const mapPaymentTypeToMethod = (type: paymentType | undefined): string | null => {
  if (!type) return null;
  switch (type) {
    case "CARD":
      return "card";
    case "BANK":
      return "bank_account";
    case "CASH":
      return "cash";
    default:
      return null;
  }
};

/**
 * Gallery-safe m2m Transfer screen — flavor B. Reproduces the "v2" flavor-b
 * look: an inline exchange icon between the two quote rows, glass summary card
 * and a dedicated footer treatment. Driven by the mock `useQuote` hook only.
 */
export const TransferFlavorBPage = memo(() => {
  const { t } = useTranslation("m2m");
  const { theme, amountState, paymentState, setQuoteState } = useStore();
  const { session } = useSessionStore();
  const { amount, anchor: amountAnchor = "send" } = amountState;
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, title } = colorConfig;
  const navigate = useNavigate();
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showDestinationPopup, setShowDestinationPopup] = useState(false);
  const [shakePayment, setShakePayment] = useState(false);

  const sourceCountry = session.data?.config?.sourceCountry || "US";
  const destinationCountry = session.data?.config?.destinationCountry || "MX";
  const sourceCurrency = COUNTRY_CURRENCY[sourceCountry] || "USD";
  const destinationCurrency = COUNTRY_CURRENCY[destinationCountry] || "MXN";

  const fundingMethod = mapPaymentTypeToMethod(paymentState.payment?.type) || "card";
  const cardId =
    paymentState.payment?.type === "CARD"
      ? paymentState.payment?.cardId
      : undefined;

  const numericAmount = amount ? parseFloat(amount) : NaN;
  const hasAmount = !Number.isNaN(numericAmount) && numericAmount > 0;
  const quoteSendAmount = hasAmount
    ? amountAnchor === "receive"
      ? Number((numericAmount / MOCK_EXCHANGE_RATE).toFixed(2))
      : numericAmount
    : null;

  const { quote, loading: quoteLoading } = useQuote(
    sourceCountry,
    destinationCountry,
    fundingMethod,
    "bank_account",
    quoteSendAmount,
    sourceCurrency,
    cardId
  );

  const blockForPricing =
    Boolean(paymentState.payment) && (quoteLoading || !quote);

  const handleConfirmClick = useCallback(() => {
    if (!paymentState.payment) {
      setShakePayment(true);
      setTimeout(() => setShakePayment(false), 500);
      return;
    }
    if (blockForPricing || !quote) {
      return;
    }
    setQuoteState(quote);
    navigate(M2M_ROUTES.SENDING);
  }, [paymentState.payment, blockForPricing, quote, setQuoteState, navigate]);

  const fee = quote?.fee ?? 0;

  const sendFormatted = useMemo(() => {
    if (quoteLoading) return "...";
    if (quote?.sendAmount == null) return undefined;
    return amountFormatter(quote.sendAmount, sourceCurrency, {
      country: "en-US",
    });
  }, [quote, quoteLoading, sourceCurrency]);

  const receiveFormatted = useMemo(() => {
    if (quoteLoading) return "...";
    if (quote?.receiveAmount == null) return undefined;
    return amountFormatter(quote.receiveAmount, destinationCurrency, {
      country: "en-US",
    });
  }, [quote, quoteLoading, destinationCurrency]);

  const totalPayment = useMemo(() => {
    if (quote?.totalCost) {
      return amountFormatter(quote.totalCost);
    }
    if (amount) {
      return amountFormatter(parseFloat(amount) + fee);
    }
    return "0";
  }, [quote, amount, fee]);

  const feeDisplay = useMemo(() => {
    return `$${fee.toFixed(2)}`;
  }, [fee]);

  const pickIcon = useCallback((type: paymentType, brand: string) => {
    switch (type) {
      case "CARD": {
        let finalIcon = null;
        if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
        if (brand.toLocaleUpperCase().includes("MASTERCARD"))
          finalIcon = masterCardLogo;

        return {
          brand,
          icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
        };
      }
      case "BANK":
        return {
          brand,
          icon: <BankIcon size={20} />,
        };
      case "CASH":
        return {
          brand,
          icon: <CashSpecialIcon size={20} />,
        };

      default:
        return {
          brand,
          icon: <WalletIcon size={20} />,
        };
    }
  }, []);

  const paymentSelected = useMemo(
    () =>
      paymentState.payment
        ? pickIcon(paymentState.payment.type, paymentState.payment.brand)
        : {
            brand: t("transfer.selectPaymentMethod"),
            icon: <WalletIcon size={20} />,
          },
    [paymentState, pickIcon, t]
  );

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="scrollable white-view-background flavor-b"
      >
        <Layout.Header>
          <Navbar
            color={title}
            title={t("transfer.title")}
            align="center"
            onBackClick={() => navigate(-1)}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section flavor-c">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section>
            <section className="estimated-quote-section">
              <section className="quote-container">
                <QuoteInfo
                  label={t("transfer.from")}
                  currency={sourceCurrency}
                  textColor={title}
                  mode="input"
                  side="send"
                  derivedAmount={
                    amountAnchor === "receive"
                      ? sendFormatted || undefined
                      : undefined
                  }
                  icon={<FlagCircle iso2={sourceCountry.toLowerCase()} />}
                />
                <ExchangeV2Icon
                  size={32}
                  className="exchange-quote-info flavor-c-icon"
                  colors={["#1dafa1", "#1dafa1"]}
                />
                <QuoteInfo
                  label={t("transfer.to")}
                  currency={destinationCurrency}
                  textColor={title}
                  mode="input"
                  side="receive"
                  derivedAmount={
                    amountAnchor === "send"
                      ? receiveFormatted || undefined
                      : undefined
                  }
                  icon={<FlagCircle iso2={destinationCountry.toLowerCase()} />}
                />
              </section>
            </section>
            <section className="payout-container payout-container-v2">
              <PayoutInfo
                title={t("transfer.payingWith")}
                subtitle={paymentSelected.brand}
                icon={paymentSelected.icon}
                color={TITLE_COLOR}
                onChange={() => setShowPaymentPopup(true)}
                className={`${!paymentState.payment ? "payment-required" : ""} ${
                  shakePayment ? "shake" : ""
                }`.trim()}
              />
              <PayoutInfo
                title={t("transfer.receiveIn")}
                subtitle={
                  formatBankAccountLabel(
                    session.data?.userData?.paymentMethod?.bankAccount?.bankName,
                    session.data?.userData?.paymentMethod?.bankAccount
                      ?.accountNumber,
                  ) ?? t("transfer.bankAccount", { last4: "1234" })
                }
                icon={<BankIcon size={20} />}
                color={TITLE_COLOR}
                onChange={() => setShowDestinationPopup(true)}
              />
              <M2mFirstSendWarning
                sendAmount={quote?.sendAmount ?? quoteSendAmount}
                sourceCurrency={sourceCurrency}
              />
              <p className="footer-text muted-foreground text-centered transfer-timing-note">
                <b>{t("transfer.instantTransfer")}</b>
                {" "}
                <span className="transfer-timing-note-rest">
                  {t("transfer.mayTakeFew")}
                </span>
              </p>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer className="flavor-b">
          <section className="footer-section">
            <section className="footer-summary summary-left gradient-card glass">
              <p className="footer-text foreground footer-summary-title">
                <b>{t("transfer.detail")}</b>
              </p>
              <p className="footer-text foreground">
                {t("transfer.fee", { amount: feeDisplay, currency: sourceCurrency })}
              </p>
              <p className="footer-text foreground">
                {t("transfer.totalPayment", {
                  amount: `$${totalPayment}`,
                  currency: sourceCurrency,
                })}
              </p>
            </section>
            <div
              className={`button-wrapper ${
                amount && paymentState.payment && quote && !quoteLoading
                  ? "rainbow-ready"
                  : ""
              }`}
            >
              <Button
                size="small"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={blockForPricing}
                onClick={handleConfirmClick}
              >
                {quoteLoading
                  ? t("transfer.loadingQuote", "Loading...")
                  : t("transfer.confirmTransfer")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup visible={showPaymentPopup} onClose={() => setShowPaymentPopup(false)}>
        <SelectPaymentMethodSlot onClose={() => setShowPaymentPopup(false)} />
      </Popup>
      <Popup
        visible={showDestinationPopup}
        onClose={() => setShowDestinationPopup(false)}
      >
        <DestinationAccountInfo onClose={() => setShowDestinationPopup(false)} />
      </Popup>
    </>
  );
});
